import React, { useState, useEffect, useRef } from 'react';
import { Heart, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL, ENDPOINTS, getImageUrl, addToCart, addToWishlist } from '../../apiConfig';
import './BestSelling.css';
import QuickViewModal from './QuickViewModal';
import img1 from '../../assets/7005b240f865103ba94dafd7ab3ee36e.jpg';
import img2 from '../../assets/f92f3ad9e5a0452775b81a945b7effbc.jpg';
import img3 from '../../assets/f5565a7eae2b771734cf6407f5eb309c.jpg';
import img4 from '../../assets/c3a13322b9cb3c12ff05a12b52dd17c2.jpg';
import img5 from '../../assets/04d1b47f7a9aa37e71ab857d6e511ec8.jpg';

/* ── Inline SVGs ── */
// eslint-disable-next-line no-unused-vars
const CartSVG = ({ id, className }) => (
  <svg id={id} className={className} xmlns="http://www.w3.org/2000/svg"
    width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

// eslint-disable-next-line no-unused-vars
const PerfumeSVG = ({ id, style }) => (
  <svg id={id} className="bs-perfume-svg" style={style} xmlns="http://www.w3.org/2000/svg"
    width="24" height="30" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2">
    <rect x="9" y="2" width="6" height="4" rx="1" fill="rgba(212, 175, 55, 0.3)" />
    <rect x="5" y="6" width="14" height="16" rx="2" stroke="#d4af37" />
    <rect x="8" y="10" width="8" height="6" rx="0.5" stroke="#d4af37" strokeWidth="1" />
    <path d="M12 10v6" strokeWidth="1" strokeOpacity="0.5" />
  </svg>
);

const CheckSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ── Static Fallback Products ── */
const staticProducts = [
  { id: 1, name: "Signature Noir", collection: "ROYAL", volume: "100 ML", price: "PKR 19,000", img: img1, rating: "★★★★★" },
  { id: 2, name: "Legacy Gold", collection: "VINTAGE", volume: "100 ML", price: "PKR 22,000", img: img2, rating: "★★★★★" },
  { id: 3, name: "Fresh Sport", collection: "ACTIVE", volume: "50 ML", price: "PKR 6,500", img: img3, rating: "★★★★★" },
  { id: 4, name: "Daily Luxe", collection: "ESSENTIAL", volume: "75 ML", price: "PKR 4,500", img: img4, rating: "★★★★" },
  { id: 5, name: "Premium Oud", collection: "PRIVATE", volume: "100 ML", price: "PKR 30,000", img: img5, rating: "★★★★★" },
  { id: 6, name: "Urban Edge", collection: "MODERN", volume: "100 ML", price: "PKR 16,000", img: img1, rating: "★★★★★" },
  { id: 7, name: "Pure Silk", collection: "FLORAL", volume: "50 ML", price: "PKR 14,000", img: img2, rating: "★★★★★" },
  { id: 8, name: "Dark Knight", collection: "NOIR", volume: "100 ML", price: "PKR 25,000", img: img3, rating: "★★★★★" },
  { id: 9, name: "Golden Hour", collection: "SIGNATURE", volume: "75 ML", price: "PKR 21,000", img: img4, rating: "★★★★★" },
  { id: 10, name: "Silver Mist", collection: "AQUA", volume: "100 ML", price: "PKR 18,500", img: img5, rating: "★★★★" },
];

const renderStars = (rating) => {
  let num;
  if (typeof rating === 'string' && rating.includes('★')) {
    num = (rating.match(/★/g) || []).length;
  } else {
    num = Number(rating) || 5;
  }
  const rounded = Math.round(num);
  const fullStars = '★'.repeat(rounded);
  const emptyStars = '☆'.repeat(5 - rounded);
  return `${fullStars}${emptyStars} ${num}/5`;
};

const BestSelling = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState(staticProducts);
  const [current, setCurrent] = useState(2);
  const [favorites, setFavorites] = useState(() => {
    try {
      const wishList = JSON.parse(localStorage.getItem('wishlistItems') || '[]');
      const favMap = {};
      wishList.forEach(item => {
        if (item.id) favMap[item.id] = true;
        if (item._id) favMap[item._id] = true;
      });
      return favMap;
    } catch { return {}; }
  });
  const [added, setAdded] = useState({});
  const [animating, setAnimating] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const timerRef = useRef(null);

  const syncFavorites = () => {
    const wishList = JSON.parse(localStorage.getItem('wishlistItems') || '[]');
    const favMap = {};
    wishList.forEach(item => {
      if (item.id) favMap[item.id] = true;
      if (item._id) favMap[item._id] = true;
    });
    setFavorites(favMap);
  };

  useEffect(() => {
    syncFavorites();
    window.addEventListener('wishlistUpdated', syncFavorites);
    return () => window.removeEventListener('wishlistUpdated', syncFavorites);
  }, []);

  useEffect(() => {
    const fetchBestSelling = async () => {
      try {
        let response = await fetch(`${BASE_URL}${ENDPOINTS.BEST_SELLING}`);
        let result = await response.json();
        
        // Support both direct array response [...], wrapped 'value' response { value: [...] }, and wrapped 'data' response { data: [...] }
        let rawProducts = Array.isArray(result)
          ? result
          : (result && Array.isArray(result.value) ? result.value : (result && Array.isArray(result.data) ? result.data : []));

        // Fallback to all products if best-selling is empty (common in new databases without orders)
        if (rawProducts.length === 0) {
          console.warn("Best selling endpoint is empty. Falling back to all products...");
          response = await fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}`);
          result = await response.json();
          rawProducts = Array.isArray(result)
            ? result
            : (result && Array.isArray(result.data) ? result.data : (result && Array.isArray(result.value) ? result.value : []));
        }

        if (rawProducts.length > 0) {
          const mapped = rawProducts.map(item => ({
            ...item,
            id: item._id || item.id,
            collection: item.brand || "ROYAL",
            volume: item.variants?.[0]?.size || item.size || item.volume || "",
            price: item.variants?.[0]?.finalPrice ? `PKR ${item.variants[0].finalPrice.toLocaleString()}` : "PKR 15,000",
            img: getImageUrl(item.images?.[0]) || img1,
            rating: item.rating || 5
          }));
          setProducts(mapped);
          setCurrent(0);
        }
      } catch (error) {
        console.error("Error fetching best selling products:", error);
      }
    };

    fetchBestSelling();
  }, []);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(
      () => setCurrent(c => (products.length === 0 ? 0 : c >= products.length - 1 ? 0 : c + 1)),
      8000
    );
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]);

  const toggleFavorite = (product) => {
    const isFav = !favorites[product.id];
    const wishList = JSON.parse(localStorage.getItem('wishlistItems') || '[]');
    let updatedWish;
    if (isFav) {
      updatedWish = [...wishList, product];
      addToWishlist(product.id || product._id).catch(console.error);
    } else {
      updatedWish = wishList.filter(item => item.id !== product.id && item._id !== product._id);
    }
    localStorage.setItem('wishlistItems', JSON.stringify(updatedWish));
    window.dispatchEvent(new Event('wishlistUpdated'));
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    const id = product.id;

    // Out of Stock check
    const isOutOfStock = 
      product.stock === 0 || 
      product.quantity === 0 || 
      product.inStock === false || 
      product.isAvailable === false ||
      product.status === 'OutOfStock' ||
      product.stockStatus === 'OutOfStock' ||
      (product.variants && product.variants.length > 0 && 
       (product.variants[0].stock === 0 || product.variants[0].quantity === 0 || product.variants[0].inStock === false));

    if (isOutOfStock) {
      window.dispatchEvent(new CustomEvent('productOutOfStock', { 
        detail: { productName: product.name } 
      }));
      return;
    }

    if (animating[id]) return;

    // Call API to add to cart
    const variantId = product.variants?.[0]?._id || product.variants?.[0]?.id || null;
    addToCart(id, variantId, 1).catch(console.error);

    setAnimating(prev => ({ ...prev, [id]: true }));
    setAdded(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setAnimating(prev => ({ ...prev, [id]: false }));
      setAdded(prev => ({ ...prev, [id]: false }));
    }, 1500);
  };

  const openQuickView = (e, product) => {
    e.stopPropagation();
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const prev = () => { setCurrent(c => (products.length === 0 ? 0 : c === 0 ? products.length - 1 : c - 1)); startTimer(); };
  const next = () => { setCurrent(c => (products.length === 0 ? 0 : c === products.length - 1 ? 0 : c + 1)); startTimer(); };

  /* ── Mobile Swipe Handlers ── */
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    if (window.innerWidth <= 768) {
      touchStartX.current = e.changedTouches[0].screenX;
    }
  };

  const handleTouchEnd = (e) => {
    if (window.innerWidth <= 768) {
      touchEndX.current = e.changedTouches[0].screenX;
      const difference = touchStartX.current - touchEndX.current;
      if (difference > 40) {
        next();
      } else if (difference < -40) {
        prev();
      }
    }
  };

  const getIndex = offset => {
    if (products.length === 0) return 0;
    return (current + offset + products.length * 5) % products.length;
  };
  const positions = [
    { offset: -2, pos: 'far-left' },
    { offset: -1, pos: 'left' },
    { offset: 0, pos: 'center' },
    { offset: +1, pos: 'right' },
    { offset: +2, pos: 'far-right' },
  ];

  return (
    <section className="bs-section">
      <div className="bs-header">
        <h2 className="bs-title">BEST <span className="bs-gold">SELLING</span></h2>
      </div>

      <div 
        className="bs-carousel-wrapper"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="bs-track">
          {positions.map(({ offset, pos }) => {
            const product = products[getIndex(offset)];
            const isCenter = pos === 'center';
            if (!product) return null;
            const isAdded = added[product.id];
            // eslint-disable-next-line no-unused-vars
            const isAnim = animating[product.id];

            return (
              <div
                key={pos}
                className={`bs-card bs-card--${pos}`}
                onClick={() => !isCenter && setCurrent(getIndex(offset))}
              >
                <div className="bs-badge">BEST SELLER</div>

                <div className="bs-img-wrap">
                  <img src={product.img} alt={product.name} className="bs-img" onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }} />
                  {isCenter && (
                    <div className="bs-action-icons">
                      <button className="bs-icon-btn" aria-label="Wishlist"
                        onClick={e => { e.stopPropagation(); toggleFavorite(product); }}>
                        <Heart size={17}
                          fill={favorites[product.id] ? '#ff3366' : 'none'}
                          color={favorites[product.id] ? '#ff3366' : 'currentColor'}
                          style={{ transition: 'all .3s ease' }}
                        />
                      </button>
                      <button
                        className="bs-icon-btn"
                        aria-label="Quick view"
                        onClick={(e) => openQuickView(e, product)}
                      >
                        <Eye size={17} />
                      </button>
                    </div>
                  )}
                </div>

                {isCenter && (
                  <div className="bs-info">
                    <span className="bs-collection">{product.collection}</span>
                    <h3 className="bs-name">{product.name}</h3>
                    <div className="bs-divider" />
                    <div className="bs-meta">
                      <span className="bs-stars">{renderStars(product.rating)}</span>
                      <div className="bs-volumes">
                        {product.variants && product.variants.length > 0
                          ? product.variants.map((v, i) => (
                              <span key={i} className="bs-volume-tag">{v.size}</span>
                            ))
                          : product.volume
                            ? <span className="bs-volume-tag">{product.volume}</span>
                            : null
                        }
                      </div>
                    </div>
                    <p className="bs-price">{product.price}</p>

                    <div className="bs-btn-wrap">
                      <button
                        id={`bs-btn-${product.id}`}
                        className={`bs-cart-btn ${isAdded ? 'is-added' : ''}`}
                        onClick={e => handleAddToCart(e, product)}
                        aria-label="Add to cart"
                      >
                        <span id={`bs-label-${product.id}`} className="bs-btn-label">
                          {isAdded ? (
                            <span className="bs-success-text">
                              <CheckSVG /> ADDED
                            </span>
                          ) : (
                            "Add to cart"
                          )}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {!isCenter && (
                  <div className="bs-side-name">{product.name}</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="bs-controls">
          <button className="bs-btn" onClick={prev}><ChevronLeft size={20} /> Prev</button>
          <div className="bs-dots">
            {Array.from({ length: Math.min(5, products.length) }).map((_, i) => {
              const numDots = Math.min(5, products.length);
              const activeDot = products.length <= 1 ? 0 : Math.round((current / (products.length - 1)) * (numDots - 1));
              return (
                <span key={i}
                  className={`bs-dot ${i === activeDot ? 'active' : ''}`}
                  onClick={() => { 
                    if (products.length > 1) {
                      const targetIndex = Math.round((i / (numDots - 1)) * (products.length - 1));
                      setCurrent(targetIndex); 
                      startTimer(); 
                    }
                  }}
                />
              );
            })}
          </div>
          <button className="bs-btn" onClick={next}>Next <ChevronRight size={20} /></button>
        </div>
      </div>
      {selectedProduct && (
        <QuickViewModal
          product={selectedProduct}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </section>
  );
};

export default BestSelling;
